/**
 * Embed Page - Entry point for TR.com embedded iframe
 *
 * This page is loaded inside an iframe on TR.com. It:
 * - Reads authentication from URL query params (?token=ptr_xxx)
 * - Provides the embed auth context
 * - Renders the chat interface without Cognito auth
 * - No sign-out button (users aren't logged in)
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { logger } from '../utils/logger';
import { EmbedAuthProvider, useEmbedAuth } from '../contexts/EmbedAuthContext';
import ChatInterface, { ChatInterfaceHandle } from '../components/chat/ChatInterface';
import SurveyModal from '../components/chat/SurveyModal';
import ChatExportMenu from '../components/chat/ChatExportMenu';
import chatAgentService, {
  buildWorkingContextFromV3,
  buildPageDataFromV3,
} from '../services/RealChatAgentService';
import { trackAIOpen, trackAIClose, setAnalyticsEmbedMode, setAnalyticsPageNameProvider } from '../utils/analytics';
import { setTrackingPageKind } from '../services/interactionTrackingService';
import { setOpenIntent } from '../components/chat/WelcomeMessage';
import { createPersonRequestGate, type PersonRequestGate } from './openIntentGate';
import { EmbedSkeleton, SkeletonTitle, UNDECIDED_HEADER } from '../components/chat/ChatSkeleton';
import { parsePdpHtml } from '../utils/parsePdpHtml';
import { parseAdobeAnalytics } from '../utils/parseAdobeAnalytics';
import type { ParentPayloadV2, ParentPayloadV3, ParentTireSize } from '../types/parentPayload';
import { getBackendApiUrl } from '../config/backend';
import { isPortalContext, shouldShowDevControls, shouldShowDevSettings } from '../utils/embedGating';
import { parentTargetOrigin } from '../utils/parentOrigin';
import { installEmbedClearBridge } from './embedClearBridge';
import { buildPageRecord } from '../services/pageRecord';
import { installOpenTracker } from '../services/openTracker';
import { installEmbedKeyboardBridge, installEmbedKeyboardState, installEmbedTapFocus } from './embedKeyboardBridge';
import { installEmbedKeyboardRide, type Ride } from './embedKeyboardRide';
import styles from '../components/chat/EmbeddableChat.module.css';
import aiLogo from '../assets/ai_chat_sparkle.svg';

/**
 * TRAC-981 (option C — branding in place): what a tap on the Tire Rack mark does.
 *
 * UNSET PLACEHOLDER — not a decision. The web team asked for the mark to link to
 * the homepage; Josh asked for it to close the chat. Both are wired up so the
 * answer is a one-value flip rather than a rewrite of the header.
 */
const LOGO_TAP: 'home' | 'exit' = 'home';
const TIRERACK_HOME_URL = 'https://www.tirerack.com/';
/** Lives in frontend/public/images; knocked out to white in CSS so it sits on
 *  the AI lane's blue and (TRAC-1060) the human-first lane's red alike. */
const TIRERACK_LOGO_SRC = '/images/tire-rack-dot-com-logo-285x65.png';

/**
 * The Tire Rack wordmark in the header's identity slot, with the LOGO_TAP
 * close-vs-home behaviour that goes with it.
 *
 * TRAC-1060: two header lanes now wear this mark — the AI lane ("AI Assist
 * BETA" on blue) and the human-first lane ("Chat" on red). The tap behaviour is
 * an unsettled decision (see LOGO_TAP above), so it lives in exactly one place:
 * a second copy would have to be flipped twice when the answer lands.
 */
const BrandMark: React.FC<{ onExit: () => void }> = ({ onExit }) =>
  LOGO_TAP === 'exit' ? (
    <button
      type="button"
      className={styles.brandMark}
      onClick={onExit}
      aria-label="Close the Tire Rack chat"
      title="Close chat"
    >
      <img src={TIRERACK_LOGO_SRC} alt="" className={styles.brandLogo} aria-hidden />
    </button>
  ) : (
    <a
      className={styles.brandMark}
      href={TIRERACK_HOME_URL}
      target="_top"
      rel="noopener"
      aria-label="Tire Rack home"
      title="Tire Rack home"
    >
      <img src={TIRERACK_LOGO_SRC} alt="" className={styles.brandLogo} aria-hidden />
    </a>
  );

function formatTireSize(t: ParentTireSize): string {
  return typeof t === 'string' ? t : `F: ${t.front} / R: ${t.rear}`;
}

/**
 * Inner component that uses the embed auth context
 *
 * Exported so the standalone /gridlife page (TRAC-757) can reuse the exact
 * same chat-only UI. It already degrades gracefully when there's no parent
 * iframe (window.parent === window), so it works standalone unchanged.
 *
 * `rootClassName` is an optional extra class applied to the widget root in every
 * state (loading/error/chat) so a standalone host (e.g. /gridlife) can reframe
 * the otherwise full-bleed embed — see GridlifePage's centered desktop card.
 *
 * `hideDevSettings` suppresses the dev-settings gear (e.g. on the public
 * /gridlife page) even in non-prod where it would otherwise show.
 */
/** TRAC-790: Chat Presence config — placement per page (a default rule + exact-page overrides). */
interface PresenceRule {
  shows: 'on' | 'off' | 'contextual';
  entry_point?: string;
  position?: string;
  opening_message?: string | null;
  starter_prompts?: string[] | null;
}
interface PresenceConfig {
  assets?: { corner_button?: { copy?: string }; proactive_image_url?: string | null };
  default: PresenceRule | null;
  overrides: (PresenceRule & { page: string })[];
}
/** Resolve the rule for a page: an override matching either the page-type token
 *  (TireDetail / TireSearchResults) or the URL path; else the default. */
function resolvePresenceRule(cfg: PresenceConfig | null, url: string, pageType?: string): PresenceRule {
  // Default-ON: an unconfigured system shows the agent WITH full page context
  // everywhere. Presence config then restricts (off) or makes contextual — it is a
  // visibility/override layer, not an opt-in gate. Defaulting to 'off' silently
  // stripped PDP/PLP context whenever nothing was set up (TRAC-790).
  if (!cfg) return { shows: 'on' };
  let path = url || '';
  try { path = new URL(url).pathname; } catch { /* url may already be a bare path */ }
  const ov = cfg.overrides?.find((o) => {
    if (!o.page) return false;
    if (pageType && o.page === pageType) return true;
    return path === o.page || path.startsWith(o.page) || (url || '').includes(o.page);
  });
  return ov ?? cfg.default ?? { shows: 'on' };
}

export const EmbedChatContent: React.FC<{ rootClassName?: string; hideDevSettings?: boolean }> = ({ rootClassName, hideDevSettings }) => {
  const { isValid, loading, error, credential } = useEmbedAuth();
  const [isSurveyModalOpen, setIsSurveyModalOpen] = useState<boolean>(false);
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [surveyEnabled, setSurveyEnabled] = useState<boolean>(false);
  const [isPreConversation, setIsPreConversation] = useState<boolean>(true);

  const handlePreConversationChange = useCallback((value: boolean) => {
    setIsPreConversation(value);
  }, []);
  // TRAC-790: Chat Presence config gates placement per page (replaces pdp/plp). `null` = not loaded.
  const [presence, setPresence] = useState<PresenceConfig | null>(null);
  const settingsLoaded = presence !== null;
  // TRAC-373: Track when page context has been received so open event can include page_name
  const [pageContextReady, setPageContextReady] = useState<boolean>(false);

  const chatInterfaceRef = useRef<ChatInterfaceHandle>(null);
  const surveyButtonRef = useRef<HTMLButtonElement>(null);
  const deleteButtonRef = useRef<HTMLButtonElement>(null);

  // TRAC-732: live-human handoff state, driven by ChatInterface via a window event.
  const [handoffState, setHandoffState] = useState<'idle' | 'connecting' | 'queued' | 'active' | 'ended'>('idle');
  const [handoffAgentName, setHandoffAgentName] = useState('Tire Rack team');
  const [handoffAgentAvatar, setHandoffAgentAvatar] = useState<string | null>(null);
  useEffect(() => {
    const onState = (e: Event) => {
      const d = (e as CustomEvent).detail || {};
      if (d.state) setHandoffState(d.state);
      if (d.state === 'idle' || d.state === 'ended' || d.state === 'connecting') {
        // Handoff over (idle/ended) OR a fresh one starting (connecting) — drop the
        // prior rep's identity so the NEXT rep can't inherit it. 'connecting' is the
        // belt-and-suspenders case: even if an 'ended' dispatch was ever missed, the
        // next handoff's first event still wipes the stale name+photo. The truthy
        // guards below never clear on their own (a later fallback/no-photo event must
        // not clobber a resolved rep), so the explicit reset has to live here.
        setHandoffAgentName(d.agentName || 'Tire Rack team');
        setHandoffAgentAvatar(d.agentAvatar || null);
      } else {
        if (d.agentName) setHandoffAgentName(d.agentName);
        if (d.agentAvatar) setHandoffAgentAvatar(d.agentAvatar);
      }
    };
    window.addEventListener('traci-handoff-state', onState);
    return () => window.removeEventListener('traci-handoff-state', onState);
  }, []);
  // TRAC-790: the human-first opening (ChatInterface owns the truth). The header is the
  // first thing the customer reads, and on this opening "AI Assist BETA" contradicts
  // everything under it — they were handed a menu of people. So it wears the headset and
  // says Chat until they either reach a rep (below: their real name and photo) or send a
  // message to the agent, which is the moment it becomes an AI conversation.
  const [humanFirst, setHumanFirst] = useState(false);
  // TRAC-790: true while we are still finding out whether anyone is on shift, on an
  // opening whose answer depends on it. Neither dress is honest yet, so the header wears
  // a neutral one rather than picking a colour it may have to take back.
  const [decidingOpening, setDecidingOpening] = useState(false);
  useEffect(() => {
    const onHumanFirst = (e: Event) => {
      const d = (e as CustomEvent).detail || {};
      setHumanFirst(!!d.active);
      setDecidingOpening(!!d.deciding);
    };
    window.addEventListener('traci-human-first', onHumanFirst);
    return () => window.removeEventListener('traci-human-first', onHumanFirst);
  }, []);
  const TIRERACK_RED = '#ce1126'; // header turns this in human mode
  // TRAC-959: the neutral header for the whole pre-decision window lives with the
  // skeleton (ChatSkeleton.tsx UNDECIDED_HEADER), since the skeleton wears it first.
  const hasClosedRef = useRef<boolean>(false);
  // TRAC-790: are we running inside the page-level shell that mounts us on every page?
  // If so, the shell owns the truth about whether the chat is actually open.
  const shellManaged = React.useMemo(
    () => new URLSearchParams(window.location.search).get('shell') === '1',
    [],
  );
  const chatOpenRef = useRef<boolean>(false);
  // TRAC-1108: the same open/closed truth as chatOpenRef, as state, so the chat can count a
  // starter-prompt exposure only once the panel is actually open.
  const [chatPanelOpen, setChatPanelOpen] = useState(false);
  const openTrackedRef = useRef<boolean>(false);
  // TRAC-1098: shared by every channel an open intent arrives on, and remembered for
  // the tab so a reopen on the next page is still a reentry.
  const personRequestGateRef = useRef<PersonRequestGate | null>(null);
  if (!personRequestGateRef.current) {
    let tabStorage: Storage | null = null;
    try { tabStorage = window.sessionStorage; } catch { /* blocked: frame memory only */ }
    personRequestGateRef.current = createPersonRequestGate(tabStorage);
  }
  // TRAC-694: the page-context listener is registered as soon as the partner
  // credential is set (decoupled from settings). Settings only gate WHICH fields
  // get stored, so they're mirrored into refs the always-on listener can read,
  // and a response that beats the settings fetch is buffered until they load.
  const presenceRef = useRef<PresenceConfig | null>(null);
  const pendingContextEventRef = useRef<MessageEvent | null>(null);
  const contextRespondedRef = useRef<boolean>(false);
  // TRAC-694: ensure the first valid context wins — both the live listener and
  // the settings-watch effect route through processContextEvent, and retries can
  // produce duplicate responses; process exactly once to avoid double staging uploads.
  const contextProcessedRef = useRef<boolean>(false);

  const env = import.meta.env.VITE_ENV || 'dev';

  // TRAC-486 / TRAC-604: see `utils/embedGating.ts` for the same-origin-iframe rule.
  const portalContext = isPortalContext();

  // TRAC-757: the minimize button posts a MINIMIZE message to the parent
  // iframe. On the standalone /gridlife page there's no parent, so hide it
  // rather than render a dead control.
  const hasParentFrame = window.parent !== window;

  // TRAC-757: widget root class — base chatWidget + any host-supplied reframing
  // class (applied across loading/error/chat states for a consistent shell).
  const widgetClass = rootClassName ? `${styles.chatWidget} ${rootClassName}` : styles.chatWidget;

  // TRAC-308: Enable analytics only on the /embed route, unless this is a health check canary
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isHealthCheck = params.get('health_check') === 'true';
    if (!isHealthCheck) {
      setAnalyticsEmbedMode(true);
      // TRAC-965: the in-frame Adobe announcements carry the tirerack.com page name (prop49)
      // the same way the older aiTireAgentResponse push does.
      setAnalyticsPageNameProvider(() => chatAgentService.getPageContext()?.page_name);
    }
    return () => { setAnalyticsEmbedMode(false); setAnalyticsPageNameProvider(null); };
  }, []);

  // Set authentication credential in chat service when available
  // TRAC-468: initSession() is now called directly from setPartnerToken() in the
  // service singleton, so it fires immediately after the token is set. Auth setup
  // stays here because the page context postMessage protocol depends on the
  // partner token being set first.
  useEffect(() => {
    if (credential) {
      chatAgentService.setPartnerToken(credential);
    }
  }, [credential]);

  // TRAC-959: tell the shell this app has painted its first frame — whatever
  // it is (skeleton, error, or chat). The shell holds its own identical
  // skeleton over the iframe until this arrives, so the app booting is never
  // a bare box.
  useEffect(() => {
    if (window.parent === window) return;
    const targetOrigin = parentTargetOrigin(env);
    window.parent.postMessage({ type: 'TRACI_UI_READY', source: 'AI-Assistant' }, targetOrigin);
  }, [env]);

  // TRAC-694: mirror the per-surface settings into refs so the always-on message
  // listener (registered on credential, before settings may have loaded) reads
  // current values without re-registering.
  useEffect(() => {
    presenceRef.current = presence;
  }, [presence]);

  // TRAC-291 / TRAC-694: parse + store a page-context response from the parent.
  // Settings gate WHICH fields are stored (read via refs), not whether we ask.
  const processContextEvent = useCallback((event: MessageEvent) => {
    // TRAC-694: process at most once. After the first valid context is stored
    // (real, or empty {} for a non-PDP page), ignore duplicate/late responses —
    // retries can produce several responses, and both the live listener and the
    // settings-watch effect call through here. Storing twice would re-fire the
    // staging upload and re-notify listeners.
    if (contextProcessedRef.current) return;
    contextProcessedRef.current = true;

    const data = event.data;
    const v2 = data as Partial<ParentPayloadV2> & { source?: string; type?: string };
    const hasV2Vehicle = !!v2.vehicle;
    const hasV2TireSize = !!v2.tireSize;
    // TRAC-822: a V3 payload is real context even with none of the V2 signals —
    // a PDP visited with no vehicle selected posts page/tireDetailData/searchSize
    // but no `vehicle`, no `tireSize`, and (in html-stripped captures) no `html`.
    // The V2-only emptiness test dropped those payloads entirely.
    const v3probe = data as Partial<ParentPayloadV3>;
    const hasV3Context = !!(v3probe.page || v3probe.tireDetailData || v3probe.searchSize
      || v3probe.selectedSize || v3probe.tireList);

    if (!data.html && !hasV2Vehicle && !hasV2TireSize && !hasV3Context) {
      // Parent acknowledged but has no context at all (non-PDP page).
      logger.debug('[Embed] Parent responded with no page context');
      chatAgentService.setPageRecord(buildPageRecord(data));   // TRAC-1212: still a page
      chatAgentService.setPageContext({});
      setPageContextReady(true);
      return;
    }

    logger.debug('[Embed] Received page context from parent', {
      type: data.type,
      source: data.source,
      url: data.url,
      title: data.title,
      v2_vehicle: hasV2Vehicle,
      v2_tireSize: hasV2TireSize,
    });

    // Start from parsePdpHtml when we have HTML — it still extracts page_name,
    // adobe_analytics, links, etc. that aren't part of the v2 structured payload.
    const pageContext = data.html ? (parsePdpHtml(data.html) || {}) : {};

    if (data.url) pageContext.page_url = data.url;

    // TRAC-790: parsePdpHtml derives a tire identity from the page <title>, which is
    // right on a tire detail page and nonsense anywhere else — the homepage opened
    // with "I see you're checking out the Tires, Wheels, Accessories, Reviews —
    // happy to help with any questions about this tire". Now that the agent runs on
    // every page, drop that identity when we have POSITIVE evidence this isn't a
    // tire detail page. No page signal at all (an older embed that predates the
    // loader) keeps the previous behavior rather than guessing.
    const pageLabel = (data as { page?: string; pageKind?: string }).page
      ?? (data as { pageKind?: string }).pageKind;
    if (pageLabel && pageLabel !== 'TireDetail' && !(data as Partial<ParentPayloadV3>).tireDetailData) {
      delete pageContext.tire_name;
      delete pageContext.tire_brand;
      delete pageContext.tire_model;
    }
    // TRAC-790: carry the page kind through so the chat can pick its opening
    // experience — tire context on the tire pages, the connect-to-a-person menu
    // everywhere else (see "The Tire Agent on Every Page").
    if (pageLabel) pageContext.page_kind = pageLabel;
    // Keep the in-chat event tracker in step, so a starter prompt shown on the cart
    // page can be told apart from the same prompt shown on a tire page.
    setTrackingPageKind(pageLabel);

    // TRAC-465: Adobe Analytics variables (always, regardless of content toggle)
    if (data.html) {
      const adobeVars = parseAdobeAnalytics(data.html);
      if (Object.keys(adobeVars).length > 0) {
        pageContext.adobe_analytics = adobeVars;
      }
    }

    // v2 structured fields override anything parsed from HTML — the parent
    // is now the source of truth for tire/vehicle.
    if (v2.vehicle) {
      pageContext.vehicle_year = String(v2.vehicle.year);
      pageContext.vehicle_make = v2.vehicle.make;
      pageContext.vehicle_model = v2.vehicle.model;
      // Dual-read: web team's PN1845900.js will ship `modelClarifier` once PN2115750 lands;
      // during the migration window we still accept the legacy `modClar` field.
      pageContext.vehicle_trim =
        v2.vehicle.modelClarifier ?? (v2.vehicle as { modClar?: string }).modClar;
      pageContext.vehicle_requires_runflat = v2.vehicle.requiresRunflat;
    }
    if (v2.tireSize) {
      pageContext.product_size = formatTireSize(v2.tireSize);
    }

    // Prefer the structured pageData's make+model for the tire name over the
    // HTML <title>-derived value. Some TR.com PDPs ship SEO-template or dev
    // placeholder strings in <title> (e.g. "Longer CrossClimate2 page title
    // for SEO data | Tire Rack") which leak into the welcome message when
    // parsePdpHtml is the only source. tireDetailData.make + model is the
    // canonical product identity TR.com already computes — use it.
    const td = (v2 as Partial<ParentPayloadV2>).tireDetailData;
    const tdMake = (td as { make?: string } | undefined)?.make;
    const tdModel = (td as { model?: string } | undefined)?.model;
    if (tdMake && tdModel) {
      pageContext.tire_name = `${tdMake} ${tdModel}`.trim();
      pageContext.tire_brand = tdMake;
      pageContext.tire_model = tdModel;
    }

    // TRAC-790: gate placement from the Chat Presence config — resolve this page's
    // rule (an exact-page override, else the default) and show unless it's 'off'.
    const v3 = data as Partial<ParentPayloadV3>;
    const hasV3 = !!(v3.page || v3.vehicle || v3.searchSize || v3.selectedSize || v3.tireDetailData);
    const workingContext = hasV3 ? buildWorkingContextFromV3(v3 as ParentPayloadV3) : null;
    // TRAC-1212: the page as the open and every AI reply record it. Before the presence
    // gate below on purpose: that decides what the agent may READ, and this is not that.
    chatAgentService.setPageRecord(buildPageRecord(v3, pageLabel, workingContext));

    // TRAC-790: a search-results page has no tire to name — it has a SEARCH. Carry the
    // size being searched so the greeting can say what the customer is actually looking
    // at. (It used to lean on a "tire name" scraped from the page title, which happened
    // to read as "2008 Toyota Sienna LE FWD Tires"; that crutch is gone now that we stop
    // inventing tires off page titles.) Reuse the same normalizer the agent gets, so the
    // greeting and the agent never disagree about the size.
    const searched = workingContext?.page_selected_size ?? workingContext?.page_searched_size;
    if (searched?.front) {
      pageContext.search_size = searched.rear && searched.rear !== searched.front
        ? `F: ${searched.front} / R: ${searched.rear}`
        : searched.front;
    }

    const presenceRule = resolvePresenceRule(presenceRef.current, data.url ?? pageContext.page_url ?? '', v3.page);
    const enabledForThisPage = presenceRule.shows !== 'off';

    if (enabledForThisPage) {
      chatAgentService.setPageContext(pageContext);
      // Sept 2026 security audit: the page's raw HTML is parsed here in the browser for
      // page facts but is NO LONGER uploaded to the backend. On a signed-in account or
      // cart page that HTML carries the customer's name, address, and orders, and
      // nothing server-side ever read it (TRAC-291 storage was debug-only).
      // TRAC-291 / TRAC-398: ship the backend-canonical working_context + pageData.
      // Only when V3 fields are present — legacy V2-only payloads still flow via pageContext.
      if (hasV3) {
        chatAgentService.setWorkingContext(workingContext);
        chatAgentService.setPageData(buildPageDataFromV3(v3 as ParentPayloadV3));
      } else {
        chatAgentService.setWorkingContext(null);
        chatAgentService.setPageData(null);
      }
    } else {
      // Off for this surface — only store page_name for analytics/Langfuse + adobe vars.
      chatAgentService.setPageContext({
        page_name: pageContext.page_name,
        adobe_analytics: pageContext.adobe_analytics,
      });
      chatAgentService.setWorkingContext(null);
      chatAgentService.setPageData(null);
    }
    logger.debug(
      '[Embed] Page context set (page=%s, enabledForThisPage=%s):',
      v3.page ?? 'unknown',
      enabledForThisPage,
      pageContext,
    );
    setPageContextReady(true);
  }, []);

  // TRAC-694: validate an incoming postMessage and route it. A response that
  // arrives before the per-surface settings load is buffered (settings decide
  // storage) and processed by the settings-watch effect below.
  const handleMessage = useCallback((event: MessageEvent) => {
    // Origin check: in prod, only accept from tirerack.com; in dev, accept all
    if (env === 'prod' && !event.origin.endsWith('.tirerack.com')) {
      return;
    }

    const data = event.data;

    // TRAC-790: how the chat was asked to open, from a page that hosts us as a plain
    // iframe rather than through our own shell (tirerack.com today: the site shows and
    // hides `.aiAgentChatContainer` itself and never calls window.TRACI.open).
    //
    // Two ways in, because one alone has a hole:
    //  - riding on the page-context answer, which the chat asks for and re-asks until
    //    answered, so it cannot be missed on a FIRST open;
    //  - this standalone message, for every open after that, where the iframe already
    //    exists and no fresh context exchange happens.
    // Handled before the page-context gate below so a repeat context answer still
    // carries an intent through, and so the standalone message needs no context at all.
    const asked = (data as { openMode?: string; mode?: string } | null);
    const askedMode = data?.type === 'TRACI_OPEN_INTENT' ? asked?.mode : asked?.openMode;
    if (data?.source === 'TR-Parent' && (askedMode === 'human' || askedMode === 'agent')) {
      setOpenIntent(askedMode);
      // TRAC-1098: a repeat of the same request is the customer coming back, not asking again.
      if (personRequestGateRef.current?.(askedMode)) chatInterfaceRef.current?.requestHuman();
    }
    if (data?.type === 'TRACI_OPEN_INTENT') return;   // intent only; no context to process

    // Accept both TR.com's actual type and our test harness type
    const isTraciContext = data?.type === 'TRACI_PAGE_CONTEXT' && data?.source === 'TR-Parent';
    const isTestContext = data?.type === 'PDP_CONTEXT' && data?.source === 'TireRack-PDP';
    if (!isTraciContext && !isTestContext) {
      return;
    }

    // A valid response arrived — stop the retry loop (the parent handler is
    // idempotent, so a stray late re-request is harmless, but no need).
    contextRespondedRef.current = true;

    const settingsReady = presenceRef.current !== null;
    if (!settingsReady) {
      // Response beat the settings fetch — buffer and let the settings-watch
      // effect process it so we don't decide storage with unknown flags.
      pendingContextEventRef.current = event;
      return;
    }
    processContextEvent(event);
  }, [env, processContextEvent]);

  // Clear-conversation context refresh: handleClearSession nulls the stored
  // pageContext, but contextProcessedRef (the once-per-load guard) stayed set,
  // so context could never re-arrive — the vehicle header chip died and the
  // welcome degraded to generic for the rest of the page's life. On the clear
  // signal, re-arm the guard and re-ask the parent (bounded retries; the
  // parent handler is idempotent).
  useEffect(() => {
    const onRefresh = () => {
      contextProcessedRef.current = false;
      contextRespondedRef.current = false;
      if (window.parent === window) return;
      const targetOrigin = parentTargetOrigin(env);
      let attempts = 0;
      const id = setInterval(() => {
        if (contextRespondedRef.current || attempts >= 6) { clearInterval(id); return; }
        attempts += 1;
        window.parent.postMessage({ type: 'TRACI_REQUEST_CONTEXT', source: 'AI-Assistant' }, targetOrigin);
      }, 400);
      window.parent.postMessage({ type: 'TRACI_REQUEST_CONTEXT', source: 'AI-Assistant' }, targetOrigin);
      logger.debug('[Embed] Context refresh requested (clear conversation)');
    };
    window.addEventListener('traci:context-refresh', onRefresh);
    return () => window.removeEventListener('traci:context-refresh', onRefresh);
  }, [env]);

  // TRAC-291 / TRAC-694: Request/response page context from TR.com parent.
  // Protocol (from TR.com PN1845900.js):
  //   1. We send:    { type: 'TRACI_REQUEST_CONTEXT' }
  //   2. Parent responds: { type: 'TRACI_PAGE_CONTEXT', source: 'TR-Parent', html, url, title, timestamp }
  //
  // TRAC-694: send on mount as soon as `credential` is set — decoupled from
  // settingsLoaded (settings gate using/storing context, not asking for it).
  // The request is retried every 500ms (parent handler is idempotent) until the
  // parent answers or an 8s cap degrades to the generic welcome. We still depend
  // on `credential` so the partner token is set before the first chat request.
  useEffect(() => {
    if (!credential) return; // Wait for auth — staging needs the partner token

    // TRAC-694: re-arm the retry latch on each (re)mount — refs persist across
    // React 18 StrictMode's mount/unmount/mount, so without this the interval
    // would clear itself on the first tick after a remount.
    contextRespondedRef.current = false;

    const hasParent = window.parent !== window;
    if (!hasParent) {
      // Standalone (not in an iframe) — no parent to wait for. Mark ready
      // immediately so downstream consumers (welcome rendering, scroll restore,
      // and the loading gate) aren't held up by the safety timeout.
      logger.debug('[Embed] No parent frame — marking pageContextReady immediately');
      setPageContextReady(true);
      return;
    }

    window.addEventListener('message', handleMessage);

    const targetOrigin = parentTargetOrigin(env);
    const sendRequest = () => {
      window.parent.postMessage(
        { type: 'TRACI_REQUEST_CONTEXT', source: 'AI-Assistant' },
        targetOrigin
      );
    };
    sendRequest();
    logger.debug('[Embed] Sent TRACI_REQUEST_CONTEXT to parent');

    // Bounded retry: re-ask until the parent responds or we hit the 8s cap.
    const retryId = setInterval(() => {
      if (contextRespondedRef.current) {
        clearInterval(retryId);
        return;
      }
      sendRequest();
      logger.debug('[Embed] Re-sent TRACI_REQUEST_CONTEXT (retry)');
    }, 500);

    // TRAC-694: 8s safety timeout — degrade to the generic welcome if the parent
    // never responds. Store empty context so the chat resolves immediately
    // instead of waiting on ChatInterface's own internal fallback.
    const timeout = setTimeout(() => {
      clearInterval(retryId);
      setPageContextReady((prev) => {
        if (!prev) {
          logger.debug('[Embed] Page context timeout (8s) — degrading to generic welcome');
          if (!chatAgentService.getPageContext()) chatAgentService.setPageContext({});
        }
        return true;
      });
    }, 8000);

    return () => {
      window.removeEventListener('message', handleMessage);
      clearInterval(retryId);
      clearTimeout(timeout);
    };
  }, [env, credential, handleMessage]);

  // TRAC-694: process a context response that arrived before settings loaded.
  useEffect(() => {
    if (settingsLoaded && pendingContextEventRef.current) {
      const ev = pendingContextEventRef.current;
      pendingContextEventRef.current = null;
      processContextEvent(ev);
    }
  }, [settingsLoaded, processContextEvent]);

  // Focus input on mount, or auto-send pending message from token-expiry reload.
  // TRAC-694: also depend on pageContextReady — the chat interface is now gated
  // behind context resolution, so chatInterfaceRef.current is null until the
  // overlay clears. Re-run when the gate opens so focus/auto-send still fire.
  useEffect(() => {
    if (isValid && pageContextReady && chatInterfaceRef.current) {
      const pendingMsg = chatAgentService.consumePendingReloadMessage();
      if (pendingMsg) {
        // TRAC-276: Auto-send the message that was in-flight when the token expired
        setTimeout(() => {
          chatInterfaceRef.current?.sendMessage(pendingMsg);
        }, 200);
      } else {
        setTimeout(() => {
          chatInterfaceRef.current?.focusInput();
        }, 100);
      }
    }
  }, [isValid, pageContextReady]);

  // Show tooltip on mount, hide after 5 seconds
  useEffect(() => {
    if (isValid) {
      setShowTooltip(true);
      const timer = setTimeout(() => {
        setShowTooltip(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isValid]);

  // Fetch survey and page content settings on mount
  useEffect(() => {
    const fetchSurveySettings = async (): Promise<void> => {
      try {
        const response = await fetch(`${getBackendApiUrl()}/api/settings/survey`);
        if (response.ok) {
          const data = await response.json();
          setSurveyEnabled(data.enabled !== false);
        }
      } catch (error) {
        logger.warn("Failed to fetch survey settings:", error);
      }
    };
    const fetchPresenceSettings = async (): Promise<void> => {
      // TRAC-790: load the Chat Presence config; it gates placement per page.
      // On failure, fall back to a permissive default so the loading sentinel still
      // resolves and we don't block postMessage handling indefinitely.
      const fallback: PresenceConfig = { default: { shows: 'on' }, overrides: [] };
      try {
        const response = await fetch(`${getBackendApiUrl()}/api/settings/presence`);
        setPresence(response.ok ? await response.json() : fallback);
      } catch (error) {
        logger.warn("Failed to fetch presence settings:", error);
        setPresence(fallback);
      }
    };
    fetchSurveySettings();
    fetchPresenceSettings();
  }, []);

  // Track AI open/close with Adobe Analytics
  // TRAC-373: Wait for page context before firing open event so page_name is included
  //
  // TRAC-790: "the chat window loaded" stopped meaning "a customer opened a chat" the
  // moment we went everywhere. The window now loads on EVERY page view, collapsed,
  // whether or not anyone touches it — so firing an open here would turn the open
  // count into a page-view count and destroy the number the everywhere-swap is judged
  // on. When we're inside the page-level shell (`?shell=1`), the shell tells us when a
  // real open happens and we count only those; a restore after navigation is not one.
  // Outside the shell (the existing embed, where the host only creates the window when
  // the customer opens chat) the original mount-time behavior is still correct.
  // TRAC-1108: whether the shell panel is open, for the experiment exposure event. Its
  // own listener, attached on mount, so an open the shell announces before the session
  // and page context are ready (a panel restored open across a navigation) is never
  // missed by the gated open/close tracking below.
  useEffect(() => {
    if (!shellManaged) return;
    const onPanelState = (event: MessageEvent) => {
      if (env === 'prod' && !event.origin.endsWith('.tirerack.com')) return;
      const d = event.data as { type?: string; source?: string } | null;
      if (!d || d.source !== 'TR-Parent') return;
      if (d.type === 'TRACI_CHAT_OPENED') setChatPanelOpen(true);
      else if (d.type === 'TRACI_CHAT_CLOSED') setChatPanelOpen(false);
    };
    window.addEventListener('message', onPanelState);
    return () => window.removeEventListener('message', onPanelState);
  }, [shellManaged, env]);

  useEffect(() => {
    if (!isValid || !pageContextReady) return;
    const pageNameNow = () => chatAgentService.getPageContext()?.page_name;

    const handleBeforeUnload = () => {
      // A close only makes sense against an open we actually counted. Guarding on
      // "is the panel open" instead would fire a close for a chat that was merely
      // restored open across a navigation and never touched — inflating closes the
      // same way opens were being inflated, just one step further along.
      if (shellManaged && !openTrackedRef.current) return;
      if (!hasClosedRef.current) {
        trackAIClose(pageNameNow());
        hasClosedRef.current = true;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    if (!shellManaged) {
      trackAIOpen(pageNameNow());
      // TRAC-1212: every open on a host that shows and hides us itself (tirerack.com),
      // not just the first of the browser session. See openTracker.ts.
      const stopOpenTracker = window.parent !== window
        ? installOpenTracker(window, chatAgentService)
        : () => {};
      return () => {
        stopOpenTracker();
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }

    const onShellState = (event: MessageEvent) => {
      if (env === 'prod' && !event.origin.endsWith('.tirerack.com')) return;
      const d = event.data as { type?: string; source?: string; genuine?: boolean; mode?: string } | null;
      if (!d || d.source !== 'TR-Parent') return;
      if (d.type === 'TRACI_CHAT_OPENED') {
        chatOpenRef.current = true;
        // Whoever opened the chat may have asked for a particular experience. Recorded
        // before anything else, because the welcome is built during this open and would
        // otherwise be built from the page's default before the request arrived.
        setOpenIntent((d.mode === 'human' || d.mode === 'agent') ? d.mode : null);
        // TRAC-790: the opening can only answer a request for a person once. On a chat
        // that already holds a conversation there is no welcome left to rebuild, and the
        // request used to vanish — press the site's "Chat with an Expert" after chatting
        // to the agent earlier and you simply got your old thread back. The chat decides
        // whether it applies (it no-ops on an empty chat, on the menu, and mid-handoff).
        // TRAC-1098: but only once per request. Minimize and reopen from the same entry
        // point and the shell repeats it; that is a reentry, which keeps the chat as it was.
        if ((d.mode === 'human' || d.mode === 'agent') && personRequestGateRef.current?.(d.mode)) {
          chatInterfaceRef.current?.requestHuman();
        }
        // `genuine` is false when the shell merely put back a panel the customer had
        // left open on the previous page. Real opens only.
        if (d.genuine === false) return;
        if (openTrackedRef.current) return;   // one open per page view
        openTrackedRef.current = true;
        hasClosedRef.current = false;
        trackAIOpen(pageNameNow());
      } else if (d.type === 'TRACI_CHAT_CLOSED') {
        chatOpenRef.current = false;
        if (openTrackedRef.current && !hasClosedRef.current) {
          trackAIClose(pageNameNow());
          hasClosedRef.current = true;
        }
      }
    };
    window.addEventListener('message', onShellState);
    return () => {
      window.removeEventListener('message', onShellState);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isValid, pageContextReady, shellManaged, env]);

  // Close delete confirmation when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        showDeleteConfirm &&
        deleteButtonRef.current &&
        !deleteButtonRef.current.contains(event.target as Node) &&
        !(event.target as Element).closest(`.${styles.deleteConfirmTooltip}`)
      ) {
        setShowDeleteConfirm(false);
      }
    };

    if (showDeleteConfirm) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDeleteConfirm]);

  const handleClearHistory = (): void => {
    if (chatInterfaceRef.current?.handleClearSession) {
      chatInterfaceRef.current.handleClearSession();
    }
    setShowDeleteConfirm(false);
  };

  // TRAC-1046 — the QA extension's New session clears the embed through this hook.
  // It is the SAME handleClearSession the header's trash button runs, so the
  // teardown order (resetHandoff() before the session id rolls over) holds by
  // construction rather than by a second implementation staying in step.
  // Deliberately a window hook and not a postMessage: this frame is cross-origin
  // to the tirerack.com page around it, so the same-origin policy is what stops a
  // page script from wiping a real customer's conversation. See embedClearBridge.ts.
  useEffect(() => installEmbedClearBridge(
    window as unknown as Record<string, unknown>,
    () => { chatInterfaceRef.current?.handleClearSession(); },
    () => chatAgentService.whenClearSettled(),
  ), []);

  // TRAC-1124: the loader unpins the phone chat from the keyboard the moment the text
  // box loses focus, instead of half a second later. See embedKeyboardBridge.ts.
  useEffect(() => installEmbedKeyboardBridge(window, parentTargetOrigin(env)), [env]);
  // TRAC-1124: the message box goes flat only while the loader reports the keyboard on screen.
  // TRAC-1152: on a framed phone the message box rides the keyboard (embedKeyboardRide.ts) and
  // a tap on the box opens the keyboard without sliding the page. The remote setting
  // phone_kb_ride turns the ride or the drag lock off with no deploy; on by default, also when
  // the setting cannot be read.
  const kbRideRef = useRef<Ride | null>(null);
  useEffect(() => installEmbedKeyboardState(window, env, () => kbRideRef.current), [env]);
  useEffect(() => {
    let live = true;
    let tapOff: (() => void) | null = null;
    const install = (opts: { ride: boolean; lock: boolean }): void => {
      const last = kbRideRef.current?.last() ?? 0;   // the keyboard may already be up when the setting lands
      kbRideRef.current?.uninstall();
      tapOff?.();
      tapOff = opts.ride ? installEmbedTapFocus(window) : null;
      kbRideRef.current = installEmbedKeyboardRide(window, opts);   // with both off it only sets the plain layout
      if (last) kbRideRef.current?.cover(last);
    };
    install({ ride: true, lock: true });
    if (kbRideRef.current) {
      fetch(`${getBackendApiUrl()}/api/settings/phone-kb-ride`)
        .then((r) => (r.ok ? r.json() : null))
        .then((s: { ride?: unknown; lock?: unknown } | null) => {
          if (!live || !s || (s.ride !== false && s.lock !== false)) return;
          install({ ride: s.ride !== false, lock: s.lock !== false });
        })
        .catch((error) => { logger.warn('Failed to fetch phone keyboard ride settings:', error); });
    }
    return () => { live = false; tapOff?.(); kbRideRef.current?.uninstall(); kbRideRef.current = null; };
  }, []);

  const toggleDeleteConfirm = (): void => {
    setShowDeleteConfirm(!showDeleteConfirm);
  };

  const handleCancelDelete = (): void => {
    setShowDeleteConfirm(false);
  };

  /**
   * Handle minimize button click
   * Sends postMessage to parent window to request minimizing the iframe
   *
   * targetOrigin comes from parentTargetOrigin (TRAC-998):
   * - Prod: the parent's own origin when it is https://www.tirerack.com or
   *   https://traci.mi.tirerack.com (our internal site), else https://www.tirerack.com.
   *   Never '*'.
   * - Dev/QA/Local: * (wildcard for flexible testing)
   *
   * TireRack.com validates on their end by checking origin ends with .tirerack.com
   */
  const handleMinimize = (): void => {
    // Fire close event when user minimizes (only once)
    // TRAC-373: Include page_name
    // TRAC-790: inside the page-level shell, the shell is the single source of truth
    // for open/close — it will relay the close back to us once it collapses the panel.
    // Firing here too would count the same close twice.
    if (!shellManaged && !hasClosedRef.current) {
      trackAIClose(chatAgentService.getPageContext()?.page_name);
      hasClosedRef.current = true;
    }

    const targetOrigin = parentTargetOrigin(env);

    window.parent.postMessage(
      {
        type: 'MINIMIZE',
        source: 'AI-Assistant',
      },
      targetOrigin
    );

    logger.debug(`[Embed] Sent MINIMIZE message to parent (origin: ${targetOrigin})`);
  };

  // TRAC-959: every pre-chat gate renders this one skeleton — the messaging
  // frame itself (header, blank canvas, inert composer), the LP-style blank
  // UI from Josh's spec. Auth → context → chat swap what's inside the frame,
  // never the frame, so the old overlay → blank shell → overlay flashing is
  // gone by construction. The page-level shell paints this same frame over
  // the iframe while the app boots (see loader.js), so the customer sees one
  // steady chat-shaped panel from first paint to first message.
  // TRAC-959: the chat's own chrome, blanked — see ChatSkeleton.tsx. The header is
  // told how many buttons it will carry so it is already that wide.
  const loadingSkeletonView = (
    <EmbedSkeleton
      className={widgetClass}
      headerButtons={
        1 /* download */
        + (shouldShowDevSettings(env) && !hideDevSettings ? 1 : 0) /* dev settings */
        + (shouldShowDevControls(env) ? 1 : 0) /* clear conversation */
        + (surveyEnabled ? 1 : 0)
        + (hasParentFrame ? 1 : 0) /* minimize */
      }
    />
  );

  // Auth loading state
  if (loading) {
    return loadingSkeletonView;
  }

  // Error state
  if (error || !isValid) {
    return (
      <div className={widgetClass}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          padding: '20px',
          textAlign: 'center',
          color: '#666'
        }}>
          <h2 style={{ color: '#dc2626', marginBottom: '10px' }}>Access Denied</h2>
          <p>{error || 'Invalid or missing embed key.'}</p>
        </div>
      </div>
    );
  }

  // TRAC-694: hold until page context resolves so the chat mounts with context
  // already present and the welcome renders once (with the tire) instead of
  // flashing a generic greeting; the 8s safety timeout flips pageContextReady
  // so this never hangs. TRAC-959: the old ~300ms grace window existed only to
  // avoid flashing the branded loader on a fast context arrival — the skeleton
  // is the same stable frame either way, so the grace machinery is gone.
  if (!pageContextReady) {
    return loadingSkeletonView;
  }

  return (
    <>
      <div className={`${widgetClass}${isPreConversation ? ` ${styles.preConversationWidget}` : ''}`} data-traci-kb-part="widget">
        {/* Widget Header - No sign out button for embed */}
        <div
          className={`${styles.widgetHeader}${isPreConversation ? ` ${styles.preConversationHeader}` : ''}`}
          style={decidingOpening ? { background: UNDECIDED_HEADER }
            : handoffState === 'active' || humanFirst ? { background: TIRERACK_RED }
            : undefined}
        >
          <div className={styles.headerLeft}>
            {decidingOpening ? (
              // TRAC-790: still finding out — a name and a colour it might have to take
              // back a beat later would be a lie. TRAC-959: so it keeps wearing the
              // skeleton's blank identity slot (decorative; the words go to screen
              // readers); the real mark and title land in place.
              <>
                <SkeletonTitle />
                <span className={styles.srOnly} role="status">Loading…</span>
              </>
            ) : handoffState === 'active' ? (
              // TRAC-732: human mode — show the LP agent's real photo + name
              <>
                {handoffAgentAvatar ? (
                  <img
                    src={handoffAgentAvatar}
                    alt=""
                    style={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover', marginRight: 9, border: '2px solid rgba(255,255,255,0.7)' }}
                  />
                ) : (
                  <span style={{ width: 30, height: 30, borderRadius: '50%', background: 'rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 9, fontSize: 15 }}>
                    {(handoffAgentName || 'T').trim().charAt(0).toUpperCase()}
                  </span>
                )}
                <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.12 }}>
                  <span style={{ fontWeight: 800, fontSize: 14 }}>{handoffAgentName}</span>
                  <span style={{ fontSize: 10, opacity: 0.85 }}>Tire Rack · live</span>
                </span>
              </>
            ) : humanFirst ? (
              // TRAC-790: on the way to a person, but nobody has picked up yet — the
              // headset and a plain "Chat", with no BETA on it. The rep's name and photo
              // replace this the moment they join.
              // TRAC-1060: the brand comes back to this lane. Dropping the wordmark was
              // a side effect of removing "AI Assist BETA", not the intent — so the slot
              // now reads wordmark | headset "Chat". Still no BETA label: TRAC-790's
              // point was that a beta badge contradicts a screen handing you a person.
              <>
                <BrandMark onExit={handleMinimize} />
                <span className={styles.brandRule} aria-hidden />
                <svg
                  width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  style={{ marginRight: 9, flex: 'none' }} aria-hidden
                >
                  <path d="M4 14v-3a8 8 0 0 1 16 0v3" />
                  <path d="M20 15v1.5a3.5 3.5 0 0 1-3.5 3.5H14" />
                  <rect x="2" y="13.5" width="4" height="6" rx="1.6" />
                  <rect x="18" y="13.5" width="4" height="6" rx="1.6" />
                </svg>
                <span className={styles.widgetTitle}>Chat</span>
              </>
            ) : (
              // TRAC-981 (option C): at phone widths the sparkle steps aside and the
              // Tire Rack wordmark takes its slot inside the same blue bar — so the
              // branding costs no vertical space and "AI Assist BETA" keeps its say.
              // The mark has to be knocked out to white to survive on #2b7088; that
              // single-colour treatment is the price this option pays. Above 767px
              // nothing changes: the sparkle stays.
              <>
                <img src={aiLogo} alt="AI Assist icon" className={styles.headerLogo} />
                <BrandMark onExit={handleMinimize} />
                <span className={styles.brandRule} aria-hidden />
                <span className={styles.widgetTitle}>
                  AI Assist<span className={styles.betaLabel}>BETA</span>
                </span>
              </>
            )}
          </div>
          <div className={styles.headerButtons}>
            {/* TRAC-732: the "Talk to a person" / "Leave" handoff control lives in an
                action row above the composer (in ChatInterface), not the header. */}
            {/* TRAC-848: download your chat. Intentionally NOT behind
                shouldShowDevControls — this is the customer-facing header, and a
                gated download button would ship to nobody but us. */}
            <ChatExportMenu />
            {/* TRACI-933: Dev Settings never renders on a prod build, any frame (shouldShowDevSettings).
                TRAC-757: also suppressed on the public /gridlife page via hideDevSettings. */}
            {shouldShowDevSettings(env) && !hideDevSettings && (
              <button
                className={styles.headerButton}
                onClick={() => chatInterfaceRef.current?.openDevSettings()}
                title="Dev Settings"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" />
                </svg>
              </button>
            )}
            {shouldShowDevControls(env) && (
              <div className={styles.deleteButtonWrapper}>
                <button
                  ref={deleteButtonRef}
                  className={styles.headerButton}
                  onClick={toggleDeleteConfirm}
                  title="Clear conversation"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M3 6h18" />
                    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                  </svg>
                </button>
                {showDeleteConfirm && (
                  <div className={styles.deleteConfirmTooltip}>
                    <div className={styles.deleteConfirmText}>
                      Clear conversation?
                    </div>
                    <div className={styles.deleteConfirmButtons}>
                      <button
                        className={styles.confirmButton}
                        onClick={handleClearHistory}
                      >
                        Confirm
                      </button>
                      <button
                        className={styles.cancelButton}
                        onClick={handleCancelDelete}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {surveyEnabled && (
              <div className={styles.surveyButtonWrapper}>
                <button
                  ref={surveyButtonRef}
                  className={styles.headerButton}
                  onClick={() => setIsSurveyModalOpen(true)}
                  title="Leave feedback"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
                    <rect x="9" y="3" width="6" height="4" rx="2" />
                    <path d="M9 12h6" />
                    <path d="M9 16h6" />
                  </svg>
                </button>
                {showTooltip && (
                  <div className={styles.tooltip}>Tell us about your chat!</div>
                )}
              </div>
            )}
            {hasParentFrame && (
              <button
                className={styles.headerButton}
                onClick={handleMinimize}
                title="Minimize chat"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              </button>
            )}
            {/* No sign out button - embed users aren't logged in */}
          </div>
        </div>

        {/* Chat Interface */}
        <div className={styles.chatContainer}>
          <ChatInterface ref={chatInterfaceRef} isPortalContext={portalContext} onPreConversationChange={handlePreConversationChange} panelOpen={!shellManaged || chatPanelOpen} />
        </div>

        {/* Survey Modal */}
        <SurveyModal
          isOpen={isSurveyModalOpen}
          onClose={() => setIsSurveyModalOpen(false)}
          userEmail={undefined}  // No email for embed users
          sessionId={chatAgentService.agentSessionId}
          partnerToken={credential}
        />
      </div>
    </>
  );
};

/**
 * Main embed page component
 */
const EmbedPage: React.FC = () => {
  return (
    <EmbedAuthProvider>
      <EmbedChatContent />
    </EmbedAuthProvider>
  );
};

export default EmbedPage;
